/**
 * ImpactAreaSynergiesChart.jsx
 *
 * Chord diagram showing co-occurrence between the 5 CGIAR Impact Areas.
 * For each result, we check which IAs it is tagged to (Principal or Significant),
 * then draw ribbons between all co-occurring IA pairs. The active IA's ribbons
 * are highlighted while others are faded.
 */

import { useState, useMemo, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { chord as d3Chord, ribbon as d3Ribbon } from 'd3-chord';
import { arc as d3Arc } from 'd3-shape';
import { descending } from 'd3-array';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';

/* ---------- IA configuration ---------- */
const IA_CONFIG = [
  { key: 'gender',       label: 'Gender Equality',     field: 'genderLevel',       color: '#009688' },
  { key: 'climate',      label: 'Climate Adaptation',  field: 'climateLevel',      color: '#2196F3' },
  { key: 'nutrition',    label: 'Nutrition & Health',   field: 'nutritionLevel',    color: '#FF9800' },
  { key: 'biodiversity', label: 'Biodiversity',         field: 'biodiversityLevel', color: '#4CAF50' },
  { key: 'poverty',      label: 'Poverty Reduction',    field: 'povertyLevel',      color: '#9C27B0' },
];

const TARGETED = new Set(['Principal', 'Significant']);

/* ---------- layout constants ---------- */
const SIZE = 520;
const CX = SIZE / 2;
const CY = SIZE / 2;
const OUTER_R = 210;
const INNER_R = 185;
const LABEL_R = OUTER_R + 18;

const ImpactAreaSynergiesChart = memo(function ImpactAreaSynergiesChart({ results, activeArea }) {
  const [tip, setTip] = useState(null);
  const [tipPos, setTipPos] = useState({ x: 0, y: 0 });
  const [hoveredChord, setHoveredChord] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);

  const activeIdx = useMemo(() => {
    const idx = IA_CONFIG.findIndex(ia => ia.key === activeArea);
    return idx >= 0 ? idx : 0;
  }, [activeArea]);

  /* ---------- build co-occurrence matrix ---------- */
  const chordData = useMemo(() => {
    if (!results || results.length === 0) return null;

    const n = IA_CONFIG.length; // 5
    const matrix = Array.from({ length: n }, () => new Float64Array(n));

    for (const r of results) {
      // Determine which IAs this result is tagged to
      const tagged = [];
      for (let i = 0; i < n; i++) {
        if (TARGETED.has(r[IA_CONFIG[i].field])) {
          tagged.push(i);
        }
      }

      // Create pairwise links
      for (let a = 0; a < tagged.length; a++) {
        for (let b = a + 1; b < tagged.length; b++) {
          matrix[tagged[a]][tagged[b]] += 1;
          matrix[tagged[b]][tagged[a]] += 1;
        }
      }
    }

    // Check if there's any data
    let hasData = false;
    for (let i = 0; i < n && !hasData; i++) {
      for (let j = 0; j < n && !hasData; j++) {
        if (matrix[i][j] > 0) hasData = true;
      }
    }
    if (!hasData) return null;

    const layout = d3Chord()
      .padAngle(0.06)
      .sortSubgroups(descending)
      .sortChords(descending)(matrix);

    return { layout };
  }, [results]);

  /* ---------- generators ---------- */
  const arcGen = useMemo(() => d3Arc().innerRadius(INNER_R).outerRadius(OUTER_R), []);
  const ribbonGen = useMemo(() => d3Ribbon().radius(INNER_R - 2), []);

  /* ---------- highlight logic ---------- */
  const isArcHighlighted = useCallback((idx) => {
    if (hoveredChord !== null) {
      return idx === hoveredChord.source.index || idx === hoveredChord.target.index;
    }
    if (hoveredNode !== null) return idx === hoveredNode;
    // Default: highlight the active area and its connections
    return true;
  }, [hoveredNode, hoveredChord]);

  const isRibbonHighlighted = useCallback((ch) => {
    if (hoveredChord !== null) {
      return ch.source.index === hoveredChord.source.index &&
             ch.target.index === hoveredChord.target.index;
    }
    if (hoveredNode !== null) {
      return ch.source.index === hoveredNode || ch.target.index === hoveredNode;
    }
    // Default: highlight ribbons connected to the active IA, fade others slightly
    return ch.source.index === activeIdx || ch.target.index === activeIdx;
  }, [hoveredNode, hoveredChord, activeIdx]);

  const getRibbonOpacity = useCallback((ch) => {
    const highlighted = isRibbonHighlighted(ch);
    if (hoveredChord !== null || hoveredNode !== null) {
      return highlighted ? 0.6 : 0.06;
    }
    // No hover: active-connected ribbons brighter, others faded
    const connected = ch.source.index === activeIdx || ch.target.index === activeIdx;
    return connected ? 0.55 : 0.15;
  }, [isRibbonHighlighted, hoveredChord, hoveredNode, activeIdx]);

  /* ---------- render ---------- */
  if (!chordData) {
    return (
      <ChartWrapper title="Impact Area Synergies">
        <NoChartData
          message="No impact area synergy data"
          detail="This chord diagram shows how results are co-tagged across the 5 CGIAR Impact Areas. No multi-IA tagged results were found in the current selection."
        />
      </ChartWrapper>
    );
  }

  const { layout } = chordData;
  const { groups } = layout;
  const chordArray = Array.from(layout);

  const exportData = chordArray.map(ch => ({
    'Impact Area A': IA_CONFIG[ch.source.index].label,
    'Impact Area B': IA_CONFIG[ch.target.index].label,
    'Shared Results': ch.source.value,
  }));

  return (
    <ChartWrapper
      title="Impact Area Synergies"
      info="Chord diagram showing how results are co-tagged across CGIAR Impact Areas. Ribbons connect IAs that share results tagged at Principal or Significant level. The currently selected IA is highlighted."
      chartData={exportData}
      exportFilename="ia-synergies"
    >
      <div className="flex justify-center relative">
        <svg
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className="w-full"
          style={{ maxWidth: SIZE, height: 'auto' }}
        >
          <defs>
            {chordArray.map((ch, i) => {
              const srcColor = IA_CONFIG[ch.source.index]?.color || '#94a3b8';
              const tgtColor = IA_CONFIG[ch.target.index]?.color || '#94a3b8';
              const srcAngle = (ch.source.startAngle + ch.source.endAngle) / 2 - Math.PI / 2;
              const tgtAngle = (ch.target.startAngle + ch.target.endAngle) / 2 - Math.PI / 2;
              return (
                <linearGradient
                  key={i}
                  id={`syn-grad-${i}`}
                  gradientUnits="userSpaceOnUse"
                  x1={CX + INNER_R * 0.5 * Math.cos(srcAngle)}
                  y1={CY + INNER_R * 0.5 * Math.sin(srcAngle)}
                  x2={CX + INNER_R * 0.5 * Math.cos(tgtAngle)}
                  y2={CY + INNER_R * 0.5 * Math.sin(tgtAngle)}
                >
                  <stop offset="0%" stopColor={srcColor} />
                  <stop offset="100%" stopColor={tgtColor} />
                </linearGradient>
              );
            })}
          </defs>

          <g transform={`translate(${CX},${CY})`}>
            {/* Ribbons */}
            {chordArray.map((ch, i) => {
              const opacity = getRibbonOpacity(ch);
              return (
                <path
                  key={`ribbon-${i}`}
                  d={ribbonGen(ch)}
                  fill={`url(#syn-grad-${i})`}
                  opacity={opacity}
                  style={{ transition: 'opacity 0.3s ease', cursor: 'pointer' }}
                  onMouseEnter={(e) => {
                    setHoveredChord(ch);
                    const src = IA_CONFIG[ch.source.index];
                    const tgt = IA_CONFIG[ch.target.index];
                    setTip({
                      type: 'chord',
                      sourceLabel: src.label,
                      targetLabel: tgt.label,
                      count: ch.source.value,
                      sourceColor: src.color,
                      targetColor: tgt.color,
                    });
                    setTipPos({ x: e.clientX, y: e.clientY });
                  }}
                  onMouseMove={(e) => setTipPos({ x: e.clientX, y: e.clientY })}
                  onMouseLeave={() => { setHoveredChord(null); setTip(null); }}
                />
              );
            })}

            {/* Arcs */}
            {groups.map((group, i) => {
              const ia = IA_CONFIG[i];
              const highlighted = isArcHighlighted(i);
              const isActive = i === activeIdx;
              const midAngle = (group.startAngle + group.endAngle) / 2;
              const lx = LABEL_R * Math.sin(midAngle);
              const ly = -LABEL_R * Math.cos(midAngle);
              const rotation = (midAngle * 180) / Math.PI;
              const flip = midAngle > Math.PI;
              return (
                <g key={`arc-${i}`}>
                  <path
                    d={arcGen(group)}
                    fill={ia.color}
                    opacity={highlighted ? 1 : 0.25}
                    stroke={isActive ? '#1f2937' : '#fff'}
                    strokeWidth={isActive ? 2 : 1}
                    style={{ transition: 'opacity 0.3s ease', cursor: 'pointer' }}
                    onMouseEnter={(e) => {
                      setHoveredNode(i);
                      setHoveredChord(null);
                      setTip({ type: 'node', label: ia.label, color: ia.color });
                      setTipPos({ x: e.clientX, y: e.clientY });
                    }}
                    onMouseMove={(e) => setTipPos({ x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => { setHoveredNode(null); setTip(null); }}
                  />
                  <text
                    x={lx}
                    y={ly}
                    dy="0.35em"
                    textAnchor={flip ? 'end' : 'start'}
                    transform={`rotate(${rotation - 90},${lx},${ly})${flip ? ` rotate(180,${lx},${ly})` : ''}`}
                    fontSize={isActive ? 11 : 10}
                    fontWeight={isActive ? 800 : 600}
                    fill={highlighted ? '#1f2937' : '#9ca3af'}
                    style={{ transition: 'fill 0.3s ease', cursor: 'pointer' }}
                    onMouseEnter={(e) => {
                      setHoveredNode(i);
                      setHoveredChord(null);
                      setTip({ type: 'node', label: ia.label, color: ia.color });
                      setTipPos({ x: e.clientX, y: e.clientY });
                    }}
                    onMouseMove={(e) => setTipPos({ x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => { setHoveredNode(null); setTip(null); }}
                  >
                    {ia.label}
                  </text>
                </g>
              );
            })}
          </g>
        </svg>

        {/* Tooltip */}
        {tip && (
          <div
            className="fixed z-50 px-3 py-2 bg-white text-gray-800 text-xs rounded-lg shadow-lg border border-gray-200 pointer-events-none"
            style={{ left: tipPos.x + 12, top: tipPos.y - 28 }}
          >
            {tip.type === 'chord' ? (
              <>
                <div className="font-semibold mb-1">
                  <span style={{ color: tip.sourceColor }}>{tip.sourceLabel}</span>
                  {' '}&#8596;{' '}
                  <span style={{ color: tip.targetColor }}>{tip.targetLabel}</span>
                </div>
                <div>{tip.count.toLocaleString()} shared results</div>
              </>
            ) : (
              <div className="font-semibold flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ backgroundColor: tip.color }} />
                {tip.label}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap items-center justify-center gap-3 text-xs text-gray-600">
        {IA_CONFIG.map((ia) => (
          <div
            key={ia.key}
            className={`flex items-center gap-1.5 ${ia.key === activeArea ? 'font-bold' : ''}`}
          >
            <span
              className="w-3 h-3 rounded-sm inline-block"
              style={{
                backgroundColor: ia.color,
                outline: ia.key === activeArea ? '2px solid #1f2937' : 'none',
                outlineOffset: 1,
              }}
            />
            <span>{ia.label}</span>
          </div>
        ))}
      </div>
    </ChartWrapper>
  );
});

ImpactAreaSynergiesChart.propTypes = {
  results: PropTypes.array.isRequired,
  activeArea: PropTypes.oneOf(['gender', 'climate', 'nutrition', 'biodiversity', 'poverty']).isRequired,
};

export default ImpactAreaSynergiesChart;
